// src/client/pages/StockReconciliation.jsx
import { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { UploadCloud, FileText, ArrowRightLeft, Search, Download, AlertTriangle, CheckCircle, HelpCircle } from "lucide-react";

ChartJS.register(ArcElement, Tooltip, Legend);

function parseFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const isText = /\.csv$/i.test(file.name);
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const wb = XLSX.read(data, { type: isText ? "string" : "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" }); // keep empty cells as ""
        resolve(json);
      } catch (e) {
        reject(e);
      }
    };
    if (isText) reader.readAsText(file);
    else reader.readAsBinaryString(file);
  });
}

function headersOf(rows) {
  if (!rows || !rows.length) return [];
  const keys = new Set();
  for (const r of rows) Object.keys(r || {}).forEach((k) => keys.add(k));
  return Array.from(keys);
}

function normalizeKey(v) {
  return String(v ?? "").trim().toLowerCase();
}

function toNumber(v) {
  const num = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, "").trim());
  return Number.isFinite(num) ? num : 0;
}

export default function StockReconciliation() {
  const [sysRows, setSysRows] = useState([]);
  const [whRows, setWhRows] = useState([]);
  const [sysHeaders, setSysHeaders] = useState([]);
  const [whHeaders, setWhHeaders] = useState([]);

  const [mapping, setMapping] = useState({
    sysName: "",
    sysQty: "",
    whName: "",
    whQty: "",
  });

  const [results, setResults] = useState([]);
  const [search, setSearch] = useState("");

  const handleUpload = async (e, isSystem) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const rows = await parseFile(file);
    const hdrs = headersOf(rows);

    if (isSystem) {
      setSysRows(rows);
      setSysHeaders(hdrs);
      // Best guess default mapping
      const nameGuess = hdrs.find(h => /sku|code|material|name|item/i.test(h)) || hdrs[0] || "";
      const qtyGuess = hdrs.find(h => /qty|quantity|onhand|stock/i.test(h)) || hdrs[1] || "";
      setMapping(m => ({ ...m, sysName: m.sysName || nameGuess, sysQty: m.sysQty || qtyGuess }));
    } else {
      setWhRows(rows);
      setWhHeaders(hdrs);
      const nameGuess = hdrs.find(h => /sku|code|material|name|item/i.test(h)) || hdrs[0] || "";
      const qtyGuess = hdrs.find(h => /qty|quantity|onhand|stock/i.test(h)) || hdrs[1] || "";
      setMapping(m => ({ ...m, whName: m.whName || nameGuess, whQty: m.whQty || qtyGuess }));
    }
    e.target.value = "";
  };

  const readyToCompare =
    sysRows.length && whRows.length &&
    mapping.sysName && mapping.sysQty && mapping.whName && mapping.whQty;

  const reconcile = () => {
    // Build maps keyed by normalized name
    const sMap = new Map();
    for (const r of sysRows) {
      const key = normalizeKey(r[mapping.sysName]);
      if (!key) continue;
      const qty = toNumber(r[mapping.sysQty]);
      // If duplicates for same key, sum
      sMap.set(key, (sMap.get(key) || 0) + qty);
    }

    const wMap = new Map();
    for (const r of whRows) {
      const key = normalizeKey(r[mapping.whName]);
      if (!key) continue;
      const qty = toNumber(r[mapping.whQty]);
      wMap.set(key, (wMap.get(key) || 0) + qty);
    }

    // Union of keys
    const all = new Set([...sMap.keys(), ...wMap.keys()]);
    const out = [];
    for (const k of all) {
      const systemQty = sMap.get(k) ?? 0;
      const warehouseQty = wMap.get(k) ?? 0;
      const difference = warehouseQty - systemQty;
      out.push({
        key: k,
        material: k, // display name
        systemQty,
        warehouseQty,
        difference,
        status:
          !sMap.has(k) ? "only_in_warehouse" :
            !wMap.has(k) ? "only_in_system" :
              difference === 0 ? "match" : "mismatch",
      });
    }
    out.sort((a, b) => a.material.localeCompare(b.material));
    setResults(out);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return results;
    return results.filter(r => r.material.includes(q));
  }, [results, search]);

  const counts = useMemo(() => {
    const match = results.filter(r => r.status === "match").length;
    const mismatch = results.filter(r => r.status === "mismatch").length;
    const onlySys = results.filter(r => r.status === "only_in_system").length;
    const onlyWh = results.filter(r => r.status === "only_in_warehouse").length;
    return { match, mismatch, onlySys, onlyWh };
  }, [results]);

  const chartData = {
    labels: ["Matched", "Mismatch", "Only in System", "Only in Warehouse"],
    datasets: [
      {
        data: [counts.match, counts.mismatch, counts.onlySys, counts.onlyWh],
        backgroundColor: ["#10b981", "#ef4444", "#3b82f6", "#f59e0b"],
        borderWidth: 0,
      },
    ],
  };

  const download = () => {
    const ws = XLSX.utils.json_to_sheet(results);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Reconciliation");
    XLSX.writeFile(wb, "stock_reconciliation.xlsx");
  };

  return (
    <div className="max-w-7xl mx-auto pb-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-navy-900 font-serif">Stock Reconciliation</h1>
        <p className="text-slate-500 mt-1">Compare system records with warehouse stock files to identify discrepancies.</p>
      </div>

      {/* Uploads */}
      <div className="grid md:grid-cols-2 gap-6 mb-8">
        <UploadCard
          label="System File"
          sublabel=".xlsx .xls .csv"
          onChange={(e) => handleUpload(e, true)}
          hasFile={sysRows.length > 0}
          fileName={sysRows.length > 0 ? `${sysRows.length} rows loaded` : null}
        />
        <UploadCard
          label="Warehouse File"
          sublabel=".xlsx .xls .csv"
          onChange={(e) => handleUpload(e, false)}
          hasFile={whRows.length > 0}
          fileName={whRows.length > 0 ? `${whRows.length} rows loaded` : null}
        />
      </div>

      {/* Column mapping */}
      {(sysRows.length > 0 || whRows.length > 0) && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          <h3 className="text-lg font-bold text-navy-900 mb-4 flex items-center gap-2">
            <ArrowRightLeft size={20} className="text-slate-400" /> Map Columns
          </h3>
          <div className="grid md:grid-cols-2 gap-8">
            {/* System Mapping */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wide border-b border-slate-100 pb-2">System Data</h4>
              {sysHeaders.length ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Material ID / SKU</label>
                    <select
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                      value={mapping.sysName}
                      onChange={(e) => setMapping(m => ({ ...m, sysName: e.target.value }))}
                    >
                      <option value="">Select Column...</option>
                      {sysHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Quantity</label>
                    <select
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                      value={mapping.sysQty}
                      onChange={(e) => setMapping(m => ({ ...m, sysQty: e.target.value }))}
                    >
                      <option value="">Select Column...</option>
                      {sysHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">Please upload a system file first.</p>
              )}
            </div>

            {/* Warehouse Mapping */}
            <div className="space-y-4">
              <h4 className="font-semibold text-slate-700 text-sm uppercase tracking-wide border-b border-slate-100 pb-2">Warehouse Data</h4>
              {whHeaders.length ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Material ID / SKU</label>
                    <select
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                      value={mapping.whName}
                      onChange={(e) => setMapping(m => ({ ...m, whName: e.target.value }))}
                    >
                      <option value="">Select Column...</option>
                      {whHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Quantity</label>
                    <select
                      className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy-900/10 focus:border-navy-900"
                      value={mapping.whQty}
                      onChange={(e) => setMapping(m => ({ ...m, whQty: e.target.value }))}
                    >
                      <option value="">Select Column...</option>
                      {whHeaders.map(h => <option key={h} value={h}>{h}</option>)}
                    </select>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-400 italic">Please upload a warehouse file first.</p>
              )}
            </div>
          </div>

          <div className="mt-8 flex justify-end">
            <button
              onClick={reconcile}
              disabled={!readyToCompare}
              className="bg-navy-900 hover:bg-navy-800 disabled:opacity-50 disabled:cursor-not-allowed text-white px-6 py-2.5 rounded-lg flex items-center gap-2 font-medium transition-all shadow-lg shadow-navy-900/20"
            >
              <Search size={18} /> Compare Files
            </button>
          </div>
        </div>
      )}

      {/* Results */}
      {!!results.length && (
        <div className="grid md:grid-cols-3 gap-6 animate-in fade-in slide-in-from-bottom-8 duration-700">
          {/* Chart Card */}
          <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200 h-fit">
            <h3 className="text-lg font-bold text-navy-900 mb-6 text-center">Overview</h3>
            <div className="w-48 mx-auto mb-6">
              <Pie data={chartData} options={{ maintainAspectRatio: true, plugins: { legend: { display: false } } }} />
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between items-center p-2 rounded bg-emerald-50 text-emerald-700">
                <div className="flex items-center gap-2"><CheckCircle size={14} /> Matched</div>
                <span className="font-bold">{counts.match}</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-red-50 text-red-700">
                <div className="flex items-center gap-2"><AlertTriangle size={14} /> Mismatch</div>
                <span className="font-bold">{counts.mismatch}</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-blue-50 text-blue-700">
                <div className="flex items-center gap-2"><HelpCircle size={14} /> Only in System</div>
                <span className="font-bold">{counts.onlySys}</span>
              </div>
              <div className="flex justify-between items-center p-2 rounded bg-amber-50 text-amber-700">
                <div className="flex items-center gap-2"><HelpCircle size={14} /> Only in Warehouse</div>
                <span className="font-bold">{counts.onlyWh}</span>
              </div>
            </div>
            <button
              onClick={download}
              className="mt-6 w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium px-4 py-2 rounded-lg transition-colors"
            >
              <Download size={16} /> Download Report
            </button>
          </div>

          {/* Table Card */}
          <div className="md:col-span-2 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col h-[600px]">
            <div className="p-4 border-b border-slate-200 flex justify-between items-center bg-slate-50">
              <h3 className="font-bold text-navy-900">Line Items</h3>
              <div className="relative w-64">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-1 focus:ring-navy-900"
                />
              </div>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="min-w-full text-left">
                <thead className="bg-slate-100 text-slate-500 uppercase text-xs font-semibold sticky top-0 z-10">
                  <tr>
                    <th className="px-6 py-3">Material / SKU</th>
                    <th className="px-6 py-3 text-right">System</th>
                    <th className="px-6 py-3 text-right">Warehouse</th>
                    <th className="px-6 py-3 text-right">Diff</th>
                    <th className="px-6 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((r, idx) => {
                    const statusColor =
                      r.status === "mismatch" ? "text-red-600 bg-red-50" :
                        r.status === "only_in_system" ? "text-blue-600 bg-blue-50" :
                          r.status === "only_in_warehouse" ? "text-amber-600 bg-amber-50" :
                            "text-emerald-600 bg-emerald-50";
                    const rowBg = r.status === 'match' ? '' : 'bg-slate-50/50';

                    return (
                      <tr key={idx} className={`hover:bg-slate-50 ${rowBg}`}>
                        <td className="px-6 py-3 text-sm font-medium text-navy-900">{r.material}</td>
                        <td className="px-6 py-3 text-sm text-right text-slate-600">{r.systemQty}</td>
                        <td className="px-6 py-3 text-sm text-right text-slate-600">{r.warehouseQty}</td>
                        <td className={`px-6 py-3 text-sm text-right font-bold ${r.difference !== 0 ? 'text-red-500' : 'text-slate-400'}`}>
                          {r.difference !== 0 ? (r.difference > 0 ? `+${r.difference}` : r.difference) : '-'}
                        </td>
                        <td className="px-6 py-3">
                          <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${statusColor}`}>
                            {r.status.replace(/_/g, " ")}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UploadCard({ label, sublabel, onChange, hasFile, fileName }) {
  return (
    <label className={`relative flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300 group ${hasFile ? 'border-emerald-500 bg-emerald-50/30' : 'border-slate-300 hover:border-navy-400 hover:bg-slate-50'}`}>
      <div className={`p-4 rounded-full mb-3 transition-colors ${hasFile ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400 group-hover:text-navy-900 group-hover:bg-white'}`}>
        {hasFile ? <FileText size={32} /> : <UploadCloud size={32} />}
      </div>
      <h4 className={`font-semibold text-lg transition-colors ${hasFile ? 'text-emerald-700' : 'text-slate-700'}`}>
        {hasFile ? "File Loaded" : label}
      </h4>
      {hasFile ? (
        <p className="text-sm text-emerald-600 font-medium mt-1">{fileName}</p>
      ) : (
        <p className="text-sm text-slate-400 mt-1">{sublabel}</p>
      )}
      <input type="file" accept=".xlsx,.xls,.csv" onChange={onChange} className="hidden" />
    </label>
  );
}
